import { capabilityOverridesActive, type OrbitMeasurementGrant } from './composite-capability.js';

/** PROVISIONAL: distinct stored skills a process remembers compiled; the oldest entry leaves first. Sized for a full skill book of a few hundred accounts. */
export const LOAD_CACHE_LIMIT = 512;

/** What the cache has done, for the tests that count compiles. */
export const loadCacheStats = { lookups: 0, hits: 0, compiles: 0 };

const isPlain = (value: object): boolean => { const prototype = Object.getPrototypeOf(value); return prototype === Object.prototype || prototype === null; };

/** Key-sorted JSON of a plain JSON-like value; throws on anything JSON would not tell apart or cannot carry. */
function canonicalJson(value: unknown): string {
  if (value === null || typeof value === 'boolean' || typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number') return Number.isFinite(value) ? JSON.stringify(value) : `"#${String(value)}"`;
  if (Array.isArray(value)) return `[${value.map((item) => (item === undefined ? 'null' : canonicalJson(item))).join(',')}]`;
  if (typeof value === 'object' && isPlain(value)) {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record).filter((key) => record[key] !== undefined).sort().map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`).join(',')}}`;
  }
  throw new TypeError('not a plain JSON value');
}

const remembered = new Map<string, unknown>();

/**
 * The loader's answer for a definition, compiled once per process, from a private copy of the definition. Only the production seed (0) is
 * remembered, and nothing is read or written while a grant or a test scope holds capabilities open,
 * because the answer then depends on state that is not part of the definition.
 */
export function loadOnce<T>(input: unknown, seed: number, grant: OrbitMeasurementGrant | undefined, load: (definition: unknown) => T): T {
  if (seed !== 0 || grant !== undefined || capabilityOverridesActive()) return load(input);
  let key: string;
  try { key = canonicalJson(input); } catch { return load(input); }
  loadCacheStats.lookups += 1;
  if (remembered.has(key)) { loadCacheStats.hits += 1; return remembered.get(key) as T; }
  // Compiled from a copy: what is remembered never aliases an object its first caller may go on to change.
  const result = load(structuredClone(input));
  loadCacheStats.compiles += 1;
  if (remembered.size >= LOAD_CACHE_LIMIT) remembered.delete(remembered.keys().next().value as string);
  remembered.set(key, result);
  return result;
}
